import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Box, Button, Typography, IconButton,
    Tabs, Tab, TextField, FormControl, InputLabel, Select, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, InputAdornment, Paper,
} from "@mui/material";
import { Close as CloseIcon, LocalShipping, Balance, Delete, Build, Add } from '@mui/icons-material';


// --- Sub-Component: Individual Form Logic ---
const TransactionForm = ({ type, inventoryItems, onAddToList }) => { // Prop renamed to onAddToList
    const [formData, setFormData] = useState({
        inventoryItemID: '',
        quantity: null,
        unitCost: 0.00,
        notes: '',
        logType: type,
    });


    useEffect(() => {
        // Reset form for next entry
        setFormData({
            inventoryItemID: '',
            quantity: null,
            unitCost: 0.00,
            notes: '',
            logType: type,
        });
    }, [type]);


    // Determine required fields and button styling based on transaction type
    const isNegative = ['SPOILAGE', 'WASTE', 'INTERNAL_USE'].includes(type);
    const requiresCost = type === 'DELIVERY';

    // Placeholder for helper text
    const qtyHelperText = isNegative
        ? "Enter quantity lost/used (e.g., 5). The system will subtract it."
        : "Enter quantity received (e.g., 50).";

    const handleAddItem = (event) => { // Renamed handleSubmit to handleAddItem
        event.preventDefault();

        // Simple client-side validation
        if (!formData.inventoryItemID || formData.quantity === null || formData.quantity <= 0) {
            alert('Please select an item and enter a valid quantity.');
            return;
        }

        // Adjust quantity sign for negative transactions (e.g., Spoilage of 5 becomes -5)
        const finalQuantityChange = isNegative && formData.quantity > 0
            ? -Math.abs(formData.quantity)
            : formData.quantity;

        const itemDetails = inventoryItems.find(i => i._id === formData.inventoryItemID);

        console.log("Adding item to list:", finalQuantityChange);

        // Call the parent function to add to the list
        onAddToList({
            ...formData,
            quantityChange: finalQuantityChange,
            item: itemDetails.item, // Add name for display
            unitOfMeasure: itemDetails.unitOfMeasure, // Add UoM
            _id: Math.random() * 10000, // Unique ID for key/removal purposes
            type: type,
        });

        // Reset form for next entry
        setFormData(prev => ({
            inventoryItemID: '',
            quantity: null,
            unitCost: requiresCost ? 0.00 : prev.unitCost, // Only reset cost if required
            notes: '',
            logType: type,
        }));
    };

    const handleFormChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'number' ? parseFloat(value) : value,
        }));
    };

    return (
        <Box component="form" onSubmit={handleAddItem} sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* Item Selector */}
            <FormControl fullWidth size="small" required>
                <InputLabel id="item-select-label">Inventory Item</InputLabel>
                <Select
                    labelId="item-select-label"
                    value={formData.inventoryItemID}
                    name="inventoryItemID"
                    label="Inventory Item"
                    onChange={handleFormChange}
                >
                    {inventoryItems.map(item => (
                        <MenuItem key={item._id} value={item._id}>
                            {item.item} ({item.id}) ({item.currentStock} {item.unit})
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* Quantity Input */}
            <TextField
                label={isNegative ? "Quantity Used/Lost" : "Quantity Received"}
                name="quantity"
                type="number"
                size="small"
                value={formData.quantity === null ? '' : formData.quantity}
                onChange={handleFormChange}
                helperText={qtyHelperText}
                required
                InputProps={{ inputProps: { min: 0.01, step: 0.01 } }}
            />

            {/* Unit Cost Input (Only for Delivery) */}
            {requiresCost && (
                <TextField
                    label="Unit Cost (at time of delivery)"
                    name="unitCost"
                    type="number"
                    size="small"
                    value={formData.unitCost}
                    onChange={handleFormChange}
                    required={requiresCost}
                    InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { min: 0.00, step: 0.01 }
                    }}
                />
            )}

            {/* Notes */}
            <TextField
                label="Notes / Reason"
                name="notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={handleFormChange}
            />

            <Button
                type="submit"
                variant="outlined" // Changed to outlined to distinguish from final submit
                color={isNegative ? 'error' : 'success'}
                startIcon={<Add />}
                sx={{ mt: 2 }}
            >
                Add to List ({type.replace('_', ' ')})
            </Button>
        </Box>
    );
};


// --- Main Modal Component ---
export const InventoryLogModal = ({ open, onClose, inventoryItems, onLogSave }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [transactionList, setTransactionList] = useState([]); // NEW: State for bulk entry list

    // Map tab index to transaction type enum
    const transactionTypes = useMemo(() => [
        { name: 'DELIVERY', icon: <LocalShipping /> },
        { name: 'COUNT_ADJUSTMENT', icon: <Balance /> },
        { name: 'SPOILAGE', icon: <Delete /> },
        { name: 'INTERNAL_USE', icon: <Build /> },
    ], []);

    // Reset tab and list on modal close
    useEffect(() => {
        if (!open) {
            setActiveTab(0);
            setTransactionList([]);
        }
    }, [open]);

    const currentType = transactionTypes[activeTab].name;

    const handleAddItemToList = (item) => {
        setTransactionList(prev => [...prev, item]);
    };

    const handleRemoveItem = (id) => {
        setTransactionList(prev => prev.filter(tx => tx._id !== id));
    };

    const handleFinalSubmit = () => {
        if (transactionList.length === 0) {
            alert("Please add at least one item to the transaction list.");
            return;
        }
        // Call parent save function with the entire list
        console.log("Submitting transaction list:", transactionList);
        onLogSave(transactionList);
        // time to send data to parent


        // onClose() is called inside onLogSave success handler in parent component usually
    };

    // --- Sub-Component: Review Table ---
    const TransactionReviewTable = () => (
        <TableContainer component={Paper} elevation={1} sx={{ maxHeight: 500 }}>
            <Typography variant="subtitle1" sx={{ p: 1, backgroundColor: '#f0f0f0' }}>
                Pending Transactions ({transactionList.length})
            </Typography>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Qty Change</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Cost</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {transactionList.length === 0 ? (
                        <TableRow><TableCell colSpan={5} align="center">Add items using the form on the left.</TableCell></TableRow>
                    ) : (
                        transactionList.map((tx, index) => (
                            <TableRow key={tx._id} sx={{ backgroundColor: tx.logType === 'DELIVERY' ? '#f0fff0' : '#fff0f0', '&:hover': { backgroundColor: '#ffe0b2' } }}>
                                <TableCell>{tx.logType.replace('_', ' ')}</TableCell>
                                <TableCell>{tx.item}</TableCell>
                                <TableCell align="right" sx={{ color: tx.quantityChange < 0 ? 'error.main' : 'success.main' }}>
                                    {tx.quantityChange} {tx.unit}
                                </TableCell>
                                <TableCell align="right">${tx.unitCost.toFixed(2)}</TableCell>
                                <TableCell>
                                    <IconButton size="small" color="error" onClick={() => handleRemoveItem(tx._id)}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
            <DialogTitle sx={{ m: 0, p: 2 }}>
                Record Bulk Inventory Transaction
                <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                    {transactionTypes.map((type, index) => (
                        <Tab
                            key={type.name}
                            label={type.name.replace('_', ' ')}
                            icon={type.icon}
                            iconPosition="start"
                        />
                    ))}
                </Tabs>

                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>

                    {/* Left Side: Single Item Form Input */}
                    <Box sx={{ flexGrow: 1, minWidth: 350, p: 2 }}>
                        <Typography variant="h6" color="primary">Add Item Details</Typography>
                        <TransactionForm
                            type={currentType}
                            inventoryItems={inventoryItems}
                            onAddToList={handleAddItemToList}
                        />
                    </Box>

                    {/* Right Side: Review Table */}
                    <Box sx={{ flexGrow: 1.5, minWidth: 400, p: 2 }}>
                        <TransactionReviewTable />
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="secondary">Cancel</Button>
                <Button
                    onClick={handleFinalSubmit}
                    color="success"
                    variant="contained"
                    disabled={transactionList.length === 0}
                    startIcon={<LocalShipping />}
                >
                    Submit {transactionList.length} Records
                </Button>
            </DialogActions>
        </Dialog>
    );
};
